import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Star, BadgeCheck } from 'lucide-react';
import { GOOGLE_REVIEWS, GoogleReview } from '../../mock/cates-content.mock';

const BRAND_LIGHT = '#1493e6';
const CARD_BG = '#111417';
const STAR = '#fbbc04';
const CLAMP_CHARS = 150;

// Google's four-colour mark. Only rendered on a review flagged `verified`, i.e.
// one that genuinely came from Google — see the note in cates-content.mock.ts.
const GoogleMark: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google" role="img">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Card: React.FC<{ review: GoogleReview }> = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const long = review.text.length > CLAMP_CHARS;
  const shown = expanded || !long ? review.text : `${review.text.slice(0, CLAMP_CHARS).trimEnd()}…`;

  return (
    <div
      style={{
        flex: '0 0 auto',
        width: 'min(88vw, 320px)',
        scrollSnapAlign: 'start',
        background: CARD_BG,
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: '1rem',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '2.6rem', height: '2.6rem', borderRadius: '50%', flexShrink: 0,
          background: review.avatarColor, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '1.05rem',
        }}>
          {review.initial}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.25 }}>
            {review.author}
          </div>
          {review.relativeDate && (
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', marginTop: '0.1rem' }}>
              {review.relativeDate}
            </div>
          )}
        </div>
        {review.verified && <GoogleMark />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} size={15} fill={STAR} stroke="none" />
        ))}
        {review.verified && (
          <BadgeCheck size={15} color={BRAND_LIGHT} style={{ marginLeft: '0.3rem' }} />
        )}
      </div>

      <p style={{ color: 'rgba(255,255,255,0.86)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>
        {shown}
      </p>

      {long && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          style={{
            alignSelf: 'flex-start', marginTop: 'auto', background: 'none', border: 'none',
            padding: 0, cursor: 'pointer', color: 'rgba(255,255,255,0.55)',
            fontSize: '0.82rem', fontWeight: 600,
          }}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

const GoogleReviewsCarousel: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const pausedRef = useRef(false);

  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  const scrollByCards = useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('div');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  // Gentle auto-advance so the strip reads as alive, looping back at the end.
  // Pauses on hover/focus, and does not run at all under reduced-motion.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      const done = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      if (done) el.scrollTo({ left: 0, behavior: 'smooth' });
      else scrollByCards(1);
    }, 4500);
    return () => window.clearInterval(id);
  }, [scrollByCards]);

  useEffect(() => {
    updateEdges();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges]);

  const arrow = (side: 'left' | 'right') => {
    const disabled = side === 'left' ? atStart : atEnd;
    return (
      <button
        type="button"
        aria-label={side === 'left' ? 'Previous reviews' : 'Next reviews'}
        onClick={() => scrollByCards(side === 'left' ? -1 : 1)}
        style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
          [side]: 0,
          width: '2.4rem', height: '2.4rem', borderRadius: '50%',
          background: 'rgba(255,255,255,0.94)', border: 'none',
          boxShadow: '0 4px 14px rgba(5,8,15,0.28)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.35 : 1, transition: 'opacity 0.2s',
          zIndex: 2,
        } as React.CSSProperties}
      >
        {side === 'left' ? <ChevronLeft size={20} color="#050608" /> : <ChevronRight size={20} color="#050608" />}
      </button>
    );
  };

  return (
    <div
      style={{ position: 'relative', padding: '0 2.25rem' }}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={() => { pausedRef.current = false; }}
    >
      {arrow('left')}
      <div
        ref={trackRef}
        style={{
          display: 'flex', gap: '1rem', overflowX: 'auto',
          scrollSnapType: 'x mandatory', scrollbarWidth: 'none',
          padding: '0.25rem 0.25rem 1rem',
        }}
        className="reviews-track"
      >
        {GOOGLE_REVIEWS.map(r => <Card key={r.author} review={r} />)}
      </div>
      {arrow('right')}
      <style>{`.reviews-track::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
};

export default GoogleReviewsCarousel;
